/**
 * SCR-029 — Public report route /r/:token
 * NO auth guard — placed outside any layout route with beforeLoad.
 * Fetches GET /api/public/reports/:token (no cookies needed).
 * Token invalid/expired/revoked/report-draft → render 404 inline.
 * Comment list read-only; "Gửi phản hồi" → /login.
 */
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { PublicReportView } from '@client/components/report/public-report-view';

export const Route = createFileRoute('/r/$token')({
  component: PublicReportPage,
});

async function fetchPublicReport(token: string) {
  // Server mounts /public/reports/:token (no /api/ prefix).
  const res = await fetch(`/public/reports/${token}`, {
    headers: { Accept: 'application/json' },
    // No credentials: public endpoint — no cookies sent
    credentials: 'omit',
  });

  if (res.status === 404) return null;

  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }

  const json = (await res.json()) as { data: unknown };
  return json.data;
}

function PublicReportPage() {
  const { token } = Route.useParams();

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['public-report', token],
    queryFn: () => fetchPublicReport(token),
    retry: (failureCount, err) => {
      // Don't retry on 404 (null result) — retry up to 2x for server errors
      if (err === null) return false;
      return failureCount < 2;
    },
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-2xl mx-auto px-4 py-8 space-y-4">
          <div className="flex justify-center py-4">
            <span className="text-lg font-bold text-primary">CLC Education</span>
          </div>
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-6 w-64" />
          <Skeleton className="h-40 w-full rounded-lg" />
          <Skeleton className="h-24 w-full rounded-lg" />
        </div>
      </div>
    );
  }

  if (isError) {
    const msg = (error as Error | null)?.message ?? '';
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4 px-4">
        <AlertCircle className="h-10 w-10 text-destructive" />
        <p className="text-base font-medium text-center">Không tải được báo cáo, thử lại sau</p>
        {msg && <p className="text-sm text-muted-foreground text-center">{msg}</p>}
        <Button variant="outline" onClick={() => void refetch()}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Thử lại
        </Button>
      </div>
    );
  }

  // data === null → token invalid / expired / revoked / report not published
  if (data === null) {
    return <NotFoundReport />;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const report = data as any;
  return <PublicReportView report={report} />;
}

function NotFoundReport() {
  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4 px-4 text-center">
      <div className="text-5xl font-bold text-muted-foreground/30">404</div>
      <h1 className="text-xl font-bold">Không tìm thấy báo cáo</h1>
      <p className="text-sm text-muted-foreground max-w-xs">
        Đường dẫn này không tồn tại, đã hết hạn, hoặc báo cáo không còn khả dụng.
      </p>
      <a
        href="/login"
        className="text-sm text-primary hover:underline underline-offset-4"
      >
        Đăng nhập vào hệ thống
      </a>
    </div>
  );
}
